import React, { useRef, useEffect } from 'react';
import './Sparkline.css';

/**
 * Sparkline Component - Crypto-style mini chart
 * Displays 7-day or 30-day point trends
 * 60px wide × 30px tall
 */
const Sparkline = ({ 
  data = [], 
  width = 60, 
  height = 30, 
  color = '#00D0FF',
  showGradient = true 
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    
    // Validate color prop to prevent invalid color strings
    const validColor = color && typeof color === 'string' && color.trim() !== '' 
      ? color 
      : '#00D0FF';
    
    // Set canvas size accounting for device pixel ratio
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);

    // Find min and max values for scaling
    const values = data.map(d => d.points || 0);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue || 1;

    // Calculate points for the line
    const points = data.map((d, index) => ({
      x: (index / (data.length - 1)) * (width - 2) + 1,
      y: height - 1 - ((d.points - minValue) / range) * (height - 2)
    }));

    // Draw gradient background if enabled
    if (showGradient && points.length > 1) {
      try {
        const gradient = ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, `${validColor}20`);
        gradient.addColorStop(1, `${validColor}05`);
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(points[0].x, height - 1);
        
        for (let i = 0; i < points.length; i++) {
          ctx.lineTo(points[i].x, points[i].y);
        }
        
        ctx.lineTo(points[points.length - 1].x, height - 1);
        ctx.closePath();
        ctx.fill();
      } catch (err) {
        console.error('Gradient creation failed:', err);
        // Skip gradient, continue with line drawing
      }
    }

    // Draw the line
    if (points.length > 1) {
      ctx.strokeStyle = validColor;
      ctx.lineWidth = 1.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      
      ctx.stroke();
    }

    // Draw data points
    ctx.fillStyle = validColor;
    points.forEach(point => {
      ctx.beginPath();
      ctx.arc(point.x, point.y, 1, 0, 2 * Math.PI);
      ctx.fill();
    });

  }, [data, width, height, color, showGradient]);

  if (!data || data.length === 0) {
    return (
      <div className="sparkline-container" style={{ width: `${width}px`, height: `${height}px` }}>
        <div className="sparkline-empty">—</div>
      </div>
    );
  }

  return (
    <div className="sparkline-container" style={{ width: `${width}px`, height: `${height}px` }}>
      <canvas ref={canvasRef} />
    </div>
  );
};

export default Sparkline;
